import { nextTick, reactive, type UnwrapNestedRefs } from 'vue';
import type { VideoJsPlayer } from 'video.js';
import { getMovieFileUrlObj, findFirstSeekPointFromHitMgiMap } from 'src/lib/moviePlayerHelper';
import type { GIMovie } from 'src/models/geoItem';
import type {
  AnalyticsParams,
  BothLeftRight,
  LeftRight,
  MoviePlayerCompareModeState,
} from 'src/models/moviePlayer';

interface Deps {
  getVjsForLr: (lr: LeftRight) => VideoJsPlayer | undefined;
  updateTitleBarTexts: (lr: LeftRight) => void;
  getUIPlayState: (lr: LeftRight) => 'playing' | 'paused';
  pauseUI: (lr_: BothLeftRight) => void;
  playUI: (lr_: BothLeftRight) => void;
  analytics: UnwrapNestedRefs<AnalyticsParams>;
}

interface MoviePlayerCompareModeMovieTransitionContext {
  setCurrentMovie: (lr: LeftRight, movie: GIMovie, movieIdx: number, playOffsetMsec: number) => Promise<void>;
  playPreviousMovie: (leftRight: LeftRight) => Promise<void>;
  playNextMovie: (leftRight: LeftRight) => Promise<void>;
  findFirstSeekPoint: (lr: LeftRight) => number;
}

async function loadMovieForLr(
  lr: LeftRight,
  movie: GIMovie,
  movieIdx: number,
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Pick<Deps, 'getVjsForLr'>,
  invalidateCurrentMovie: (lr: LeftRight) => void,
): Promise<boolean> {
  if (state[lr].movieList.current.currentMovie && state[lr].movieList.current.currentMovie.id === movie.id) {
    // 現在再生中と同一動画のため取得不要
    return true;
  }
  // 現在再生しているのと異なる動画なので取得する
  state[lr].movieList.current.currentMovie = movie;
  state[lr].movieList.current.currentMovieIdx = movieIdx;
  state[lr].movieList.current.isFirstMovie = movieIdx === 0;
  const currentMl = state[lr].movieList.listSelection.currentMovieList;
  state[lr].movieList.current.isLastMovie = !!currentMl && movieIdx === currentMl.movies.length - 1;

  let urlObj;
  try {
    urlObj = await getMovieFileUrlObj(movie.id);
  } catch (e) {
    console.error('動画ファイルURLの取得に失敗しました', e);
    invalidateCurrentMovie(lr);
    state.ui.activeVjsLrMap[lr] = false;
    return false;
  }
  const vjs = deps.getVjsForLr(lr);
  const url = urlObj[state.ui.selectedCameraPosition];
  if (!url) {
    invalidateCurrentMovie(lr);
    state.ui.activeVjsLrMap[lr] = false;
    return false;
  }
  if (!vjs) return true;

  vjs.src(url);
  state.ui.activeVjsLrMap[lr] = true;
  await new Promise<void>((resolve) => {
    vjs.one('loadeddata', () => { resolve(); });
  });
  return true;
}

function findMatchedMgi(
  mgis: NonNullable<GIMovie['movieGeoIndices']>,
  realOffsetMsec: number,
): { matchedMgi: NonNullable<GIMovie['movieGeoIndices']>[number] | null; matchedMgiIdx: number } {
  let matchedMgi = null;
  let matchedMgiIdx = -1;
  for (let i = 0, len = mgis.length; i < len; i++) {
    matchedMgi = mgis[i];
    matchedMgiIdx = i;
    if (matchedMgi.endMsecDiff && realOffsetMsec < matchedMgi.endMsecDiff) {
      break;
    }
  }
  return { matchedMgi, matchedMgiIdx };
}

// useMoviePlayerCompareModePlayback.tsから分割した動画切替
// （前後の動画への遷移・シーク位置解決）関連の関数。
// vjsインスタンス自体はuseMoviePlayerCompareModeVjs.tsが保持し、再生状態の保存・復元は
// useMoviePlayerCompareModeControl.tsに委譲するため、
// 本ファイルはdeps経由でのみそれらにアクセスする。
export function useMoviePlayerCompareModeMovieTransition(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModeMovieTransitionContext> {
  const invalidateCurrentMovie = (lr: LeftRight) => {
    state[lr].movieList.current.currentMovie = null;
    state[lr].movieList.current.currentMovieIdx = -1;
    state[lr].movieList.current.isFirstMovie = false;
    state[lr].movieList.current.isLastMovie = false;
    state[lr].geo.currentMgi = null;
    state[lr].geo.currentMgiIdx = -1;
    state[lr].geo.currentMovieCurrentDt = null;
  };
  const setCurrentMovie = async(lr: LeftRight, movie: GIMovie, movieIdx: number, playOffsetMsec: number) => {
    const realOffsetMsec = (movie.playStartOffsetMsec || 0) + playOffsetMsec;
    // 一旦再生を止める
    const playStateLeft = deps.getUIPlayState('left');
    const playStateRight = deps.getUIPlayState('right');
    deps.pauseUI('left');
    deps.pauseUI('right');
    state[lr].isMovieReady = false;

    const loaded = await loadMovieForLr(lr, movie, movieIdx, state, deps, invalidateCurrentMovie);
    if (!loaded) return;

    // mgiを計算する
    const mgis = state[lr].movieList.current.currentMovie?.movieGeoIndices;
    if (!mgis) return;

    const { matchedMgi, matchedMgiIdx } = findMatchedMgi(mgis, realOffsetMsec);
    state[lr].geo.currentMgi = matchedMgi;
    state[lr].geo.currentMgiIdx = matchedMgiIdx;
    deps.updateTitleBarTexts(lr);
    const vjs = deps.getVjsForLr(lr);
    if (vjs) {
      vjs.currentTime(realOffsetMsec / 1000);
    }
    if (lr === 'left') {
      deps.analytics.tmpCalcBase = realOffsetMsec;
    }
    nextTick(() => {
      // 元々再生中だった場合は、再生を再開
      if (playStateLeft === 'playing') {
        deps.playUI('left');
      }
      if (playStateRight === 'playing') {
        deps.playUI('right');
      }
      state[lr].isMovieReady = true;
    });
  };
  const playPreviousMovie = async(leftRight: LeftRight) => {
    if (state[leftRight].movieList.current.isTryingToPlayAnotherMovie || state[leftRight].movieList.current.isFirstMovie) return;

    state[leftRight].movieList.current.isTryingToPlayAnotherMovie = true;
    // setCurrentMovie が例外を投げてもロックが残らないよう finally で解除する
    try {
      const prevMovieIdx = state[leftRight].movieList.current.currentMovieIdx - 1;
      const prevMovie = state[leftRight].movieList.listSelection.currentMovieList?.movies[prevMovieIdx];
      if (prevMovie && prevMovieIdx !== undefined) {
        const offsetMsec = state[leftRight].playback.isPlayBackward ? (prevMovie.durationMsec || 0) : 0;
        await setCurrentMovie(
          leftRight,
          prevMovie,
          prevMovieIdx,
          offsetMsec,
        );
      }
    } finally {
      state[leftRight].movieList.current.isTryingToPlayAnotherMovie = false;
    }
  };
  const playNextMovie = async(leftRight: LeftRight) => {
    if (state[leftRight].movieList.current.isTryingToPlayAnotherMovie || state[leftRight].movieList.current.isLastMovie) return;

    state[leftRight].movieList.current.isTryingToPlayAnotherMovie = true;
    // setCurrentMovie が例外を投げてもロックが残らないよう finally で解除する
    try {
      const nextMovieIdx = state[leftRight].movieList.current.currentMovieIdx + 1;
      const nextMovie = state[leftRight].movieList.listSelection.currentMovieList?.movies[nextMovieIdx];
      if (nextMovie && nextMovieIdx !== undefined) {
        await setCurrentMovie(
          leftRight,
          nextMovie,
          nextMovieIdx,
          0,
        );
      }
    } finally {
      state[leftRight].movieList.current.isTryingToPlayAnotherMovie = false;
    }
  };
  const findFirstSeekPoint = (lr: LeftRight): number => {
    const currentMovieList = state[lr].movieList.listSelection.currentMovieList;
    if (!currentMovieList) return 0;

    return findFirstSeekPointFromHitMgiMap(currentMovieList);
  };

  return reactive({ setCurrentMovie, playPreviousMovie, playNextMovie, findFirstSeekPoint });
}
